import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ScrollText, Upload, ShieldCheck } from 'lucide-react';
import { btnPrimary, btnGhost, FormModal } from '../components/shared/FormKit';
import { fetchJSON } from '../utils/api';
import SettingsActivityLog from './SettingsActivityLog';
import SettingsLegacyImport from './SettingsLegacyImport';

// Admin Settings — the one page for the Admin-only tools (gated by the
// Admin-only key admin:settings, docs/modules/auth-admin.md §8):
//   Activity Log   what the system did (audit_logs, read-only)
//   Legacy Import  the "Upload signed form" switch
//   Security       clear lockouts and sign-in counters
// Each tab is its own panel component; this page carries the chrome and the
// tab bar (`?tab=` keeps the old /settings/activity-log and
// /settings/legacy-import links landing on the right tab).

const TABS = [
  { id: 'activity', name: 'Activity Log', icon: ScrollText },
  { id: 'legacy-import', name: 'Legacy Import', icon: Upload },
  { id: 'security', name: 'Security', icon: ShieldCheck },
];

const fmtWhen = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-SG', { timeZone: 'Asia/Singapore', hour12: false });
};

// What POST /api/auth/reset-security clears — all in-memory counters, so the
// confirm modal can promise "no data, password or permission is touched".
const RESET_CLEARS = [
  'Failed sign-in counters',
  'Account lockouts (5 failed sign-ins)',
  'IP lockouts and suspicious-activity scores',
  'Verification codes (OTP) not yet used',
  'Rate-limit counters',
];

const SecurityPanel = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { clearedData, timestamp }

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await fetchJSON('/api/auth/reset-security', { method: 'POST' });
      setDone({ clearedData: data.clearedData || [], timestamp: data.timestamp });
      setOpen(false);
    } catch (e) {
      setError(e.message || 'Security reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-slate-500 mb-4">Sign-in protection keeps its counters in the server&apos;s memory. Clear them when someone is locked out and cannot wait for the lockout to expire.</p>

      <section className="bg-white rounded-lg border border-slate-200 p-5 sm:p-6 flex flex-wrap items-start gap-5">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-slate-900">Security Reset</h2>
          <p className="text-sm text-slate-600 mt-1">Unblocks anyone currently locked out and resets every sign-in counter. It clears:</p>
          <ul className="list-disc pl-5 text-sm text-slate-700 mt-2 space-y-0.5">
            {RESET_CLEARS.map((item) => <li key={item}>{item}</li>)}
          </ul>
          <p className="text-xs text-slate-500 mt-3">No account, password, permission or record is changed. The reset itself is written to the Activity Log.</p>
        </div>
        <button
          type="button"
          onClick={() => { setError(''); setOpen(true); }}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 shrink-0"
        >
          <ShieldCheck className="w-4 h-4" />
          Clear Lockouts & Counters
        </button>
      </section>

      {done && (
        <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <div className="font-medium">Security counters cleared{done.timestamp ? ` at ${fmtWhen(done.timestamp)}` : ''}.</div>
          <ul className="mt-1 list-disc pl-5 text-green-700">
            {done.clearedData.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </div>
      )}

      {open && (
        <FormModal
          title="Security Reset"
          size="md"
          onClose={busy ? null : () => setOpen(false)}
          footer={(
            <>
              <button type="button" onClick={() => setOpen(false)} disabled={busy} className={btnGhost}>Cancel</button>
              <button type="button" onClick={run} disabled={busy} className={btnPrimary}>
                {busy ? 'Clearing…' : 'Clear Now'}
              </button>
            </>
          )}
        >
          <p className="text-sm text-slate-700">Everyone currently locked out will be able to sign in again straight away, and every failed-attempt counter starts from zero.</p>
          <p className="text-xs text-slate-500">Nothing else changes.</p>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </FormModal>
      )}
    </div>
  );
};

const SettingsAdmin = () => {
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab = TABS.some((t) => t.id === requested) ? requested : TABS[0].id;
  const pick = (id) => setParams(id === TABS[0].id ? {} : { tab: id }, { replace: true });

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-5">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Admin Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Tools for administrators only: the system&apos;s activity log, the legacy import switch and sign-in protection.</p>
      </div>

      <div className="border-b border-slate-200 mb-5 flex flex-wrap gap-1">
        {TABS.map(({ id, name, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => pick(id)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {name}
          </button>
        ))}
      </div>

      {tab === 'activity' && <SettingsActivityLog />}
      {tab === 'legacy-import' && <SettingsLegacyImport />}
      {tab === 'security' && <SecurityPanel />}
    </div>
  );
};

export default SettingsAdmin;
